import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Shield, Award, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { countryCodes, pricingPlans, testimonials } from '../data/mock';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [phoneError, setPhoneError] = useState('');

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    // Limit to 11 digits
    const limitedValue = numericValue.slice(0, 11);
    
    setFormData({ ...formData, phone: limitedValue });
    
    // Validation feedback
    if (numericValue.length > 11) {
      setPhoneError('Maximum 11 digits allowed');
    } else if (value !== numericValue) {
      setPhoneError('Only numbers are allowed');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.phone && (formData.phone.length > 11 || !/^\d+$/.test(formData.phone))) {
      setPhoneError('Please enter a valid phone number (max 11 digits)');
      return;
    }
    
    console.log('Landing page form submitted:', formData);
    toast.success('Thank you! We will contact you shortly.');
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', message: '' });
    setPhoneError('');
  };

  return (
    <>
      <Helmet>
        <title>Trademark Registration from $45 - Trademark Credentials | Fast & Affordable</title>
        <meta name="description" content="Protect your brand starting at just $45! Fast trademark registration, expert support, high success rate. Join 10,000+ satisfied clients. Get started in minutes!" />
        <meta name="keywords" content="affordable trademark registration, fast trademark filing, cheap trademark services, brand protection starting $45" />
      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero Section with Form */}
      <section className="pt-20 pb-4 md:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50/40 via-white to-purple-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-purple-100/30 text-purple-500 px-4 py-2 rounded-full text-sm font-semibold">
                  #1 Trademark Registration Service in USA
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                Protect Your Brand
                <span className="bg-gradient-to-r from-purple-200 to-purple-300 bg-clip-text text-transparent">
                  {' '}Starting at $45
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Fast, reliable trademark registration with expert support. Join thousands of businesses who trust us to protect their brand identity.
              </p>
              <div className="flex items-center space-x-8 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-purple-700" />
                  <span className="text-gray-700 font-medium">24-Hour Filing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-purple-700" />
                  <span className="text-gray-700 font-medium">100% Secure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="h-6 w-6 text-purple-700" />
                  <span className="text-gray-700 font-medium">Expert Team</span>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 border border-purple-100">
              <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-6 rounded-2xl mb-6 text-center">
                <h2 className="text-2xl font-bold mb-2">Start Your Registration</h2>
                <p className="text-purple-50">Fill out the form to get started today</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                  <div className="flex space-x-2">
                    <Select
                      value={formData.countryCode}
                      onValueChange={handleCountryCodeChange}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="Phone number"
                      className={`flex-1 ${phoneError ? 'border-red-500' : ''}`}
                      maxLength={11}
                    />
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your trademark needs..."
                    rows={3}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Started Now'}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees
            </p>
          </div>

          {/* Mobile Slider */}
          <div className="lg:hidden overflow-x-auto pb-4">
            <div className="flex space-x-4 px-4">
              {pricingPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden ${
                    plan.recommended ? 'ring-2 ring-purple-300' : ''
                  }`}
                >
                  {plan.recommended && (
                    <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white px-4 py-2 text-sm font-semibold text-center">
                      Recommended
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-purple-700">{plan.price}</span>
                      <p className="text-sm text-gray-600 mt-1">{plan.fee}</p>
                    </div>
                    <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Check className="h-5 w-5 text-purple-700 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mb-4">{plan.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                  plan.recommended ? 'ring-2 ring-purple-300 transform scale-105' : ''
                }`}
              >
                {plan.recommended && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-purple-800 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    Recommended
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-purple-700">{plan.price}</span>
                    <p className="text-sm text-gray-600 mt-1">{plan.fee}</p>
                  </div>
                  <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <Check className="h-5 w-5 text-purple-700 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{plan.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-xl text-gray-600">
              See what our clients say about us
            </p>
          </div>

          {/* Mobile Slider */}
          <div className="lg:hidden overflow-x-auto pb-4">
            <div className="flex space-x-4 px-4">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="flex-shrink-0 w-80 bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-purple-700">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-purple-700">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-br from-purple-100/20 to-purple-200/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Don't Wait - Protect Your Brand Today
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of satisfied clients who trusted us with their trademark registration
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-200 hover:to-purple-300 text-white px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Start Your Application Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2025 Trademark Credentials. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
